import React, { useEffect, useMemo } from 'react';

import { Form, InputNumber, Select, Button, Space, Card, Row, Col, message } from 'antd';
import { useTranslation } from 'react-i18next';

import ProductSelect from '@components/shared/products/ProductSelect';
import ShelfSelectImpl from '@components/shared/selects/ShelfSelect';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

import { replenishmentService } from '@services/operator/replenishmentService';
import type { ReplenishmentSlot } from '@services/operator/replenishmentService';

// merchantInventoryTypes 來自 OperatorDataContext，shape 為
//   [{ merchant_id, enabled, inventory_type: { id, name } }]
// （對齊 StockTransferOrders/New.tsx 與 ProductionBoms/Edit.tsx 的消費方式）。
interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface SlotFormValues {
  product_id?: string;
  inventory_type_id?: string | null;
  shelf_id?: string;
  capacity?: number;
  replenishment_threshold?: number;
}

export interface SlotFormProps {
  // 由父層 (<MerchantSelect> 的 render-prop / Edit 載入的 record merchant) 提供的 merchant，
  // 驅動 ProductSelect 的搜尋範圍與 inventory_type 選項過濾。新增前尚未選 merchant 時為 null。
  merchantId: string | null;
  mode: 'new' | 'edit';
  // 編輯模式預填：capacity / threshold / inventory_type_id + product/shelf 的 initialOption。
  initialSlot?: ReplenishmentSlot | null;
  // 編輯模式在 merchantDataLoading 期間 disabled，避免短暫顯示前一個 merchant 的清單。
  disabled?: boolean;
  // 儲存成功後回呼（通常導回列表）。
  onSaved: () => void;
  // 取消 / 放棄編輯回呼（通常導回列表）；與其他 New/Edit 頁的「取消」按鈕對齊。
  onCancel: () => void;
}

/**
 * SlotForm — 揀補儲位 slot 設定的共用表單（新增 / 編輯共用）。
 *
 * 後端 (product_replenishment_shelves_controller.rb)：
 *   - create 接受 product_id / shelf_id / inventory_type_id / capacity / replenishment_threshold
 *   - update 只接受 capacity / replenishment_threshold / inventory_type_id
 *     （product + shelf 是 slot 的識別鍵，不可改）。
 *   shelf 綁定 current_warehouse、product 綁定 accessible merchant。
 *
 * 商家範圍鎖定：本元件「不」內含 <MerchantSelect>；merchantId 一律由父層提供——
 *   - 新增：父層 <MerchantSelect> 的 render-prop 帶入選到的 merchantId，
 *     inventory_type 下拉的選項才會被正確帶出（修掉新增模式下拉空白的 bug）。
 *   - 編輯：父層 (Edit.tsx) 透過 useRecordMerchantSync 把 currentMerchant 對齊到
 *     slot 所屬 merchant，再把 currentMerchant?.id 當 merchantId 傳進來。
 */
const SlotForm: React.FC<SlotFormProps> = ({
  merchantId,
  mode,
  initialSlot = null,
  disabled = false,
  onSaved,
  onCancel,
}) => {
  // ShelfSelect 仍是 .jsx；TS 從 destructuring 推導出 value/onChange 為必填，但 Form.Item 會在 runtime 注入。
  // 放在 component body 內避免 Vite HMR top-level TDZ（對齊 ProductEdit.tsx 的 cast 慣例）。
  const ShelfSelect = ShelfSelectImpl as React.ComponentType<any>;

  const { t } = useTranslation();
  const [form] = Form.useForm<SlotFormValues>();
  const isEdit = mode === 'edit';

  const { merchantInventoryTypes } = useOperatorData();
  // 顯式把 merchantId 帶進 product service hook（對齊 PurchaseOrders/Edit.tsx），讓商品搜尋的
  // merchant scope 直接綁定 merchantId，而非依賴 ProductSelect prop 的 param 優先序。
  const productService = useOperatorProductService({ merchantId: merchantId ?? undefined });

  // 編輯模式預填既有值；新增模式 / initialSlot 清空時 resetFields。
  // 現行路由下 New 與 Edit 是各自獨立、會 unmount/remount 的頁面，單一 SlotForm 實例
  // 不會在原地從 edit 切回 new；此處仍補 else resetFields 作防禦，避免未來若有 caller
  // 重用同一個 mounted 實例並把 initialSlot 由 slot 改回 null 時殘留舊值。
  useEffect(() => {
    if (initialSlot) {
      form.setFieldsValue({
        product_id: initialSlot.product_id,
        inventory_type_id: initialSlot.inventory_type_id,
        shelf_id: initialSlot.shelf_id,
        capacity: initialSlot.capacity ?? undefined,
        replenishment_threshold: initialSlot.replenishment_threshold ?? undefined,
      });
    } else {
      form.resetFields();
    }
    // form 為穩定 ref；以 initialSlot 身分作為預填觸發點。
  }, [initialSlot, form]);

  const inventoryTypeOptions = useMemo(() => {
    if (!merchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === merchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type)
      .filter(Boolean)
      .map(it => ({ value: it.id, label: it.name ?? it.id }));
  }, [merchantInventoryTypes, merchantId]);

  // 編輯時 product / shelf 為 async-search 下拉，需要把既有選項預填，否則只會顯示原始 id。
  const productDefaultOptions = useMemo(() => {
    if (!initialSlot) return [];
    const sku = initialSlot.product_sku ?? '';
    const name = initialSlot.product_name ?? '';
    // label 對齊 useProductSearch 的 `${sku} - ${name}` 顯示格式，避免編輯時商品欄
    // 退化成裸 SKU（看不出品名）。任一欄缺值時退而顯示有值者，最後才用 product_id。
    const label = sku && name ? `${sku} - ${name}` : (sku || name || initialSlot.product_id);
    return [{
      value: initialSlot.product_id,
      label,
      sku,
      name,
    }];
  }, [initialSlot]);

  const shelfInitialOption = useMemo(() => {
    if (!initialSlot) return undefined;
    return {
      value: initialSlot.shelf_id,
      label: initialSlot.shelf_location ?? initialSlot.shelf_id,
      location: initialSlot.shelf_location ?? initialSlot.shelf_id,
    };
  }, [initialSlot]);

  const handleSubmit = async () => {
    let values: SlotFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // antd 表單驗證錯誤（errorFields）— 由 Form.Item 就地顯示，不需另外處理。
      return;
    }

    try {
      const response = isEdit && initialSlot
        ? await replenishmentService.updateShelf(initialSlot.id, {
            inventory_type_id: values.inventory_type_id ?? null,
            capacity: values.capacity,
            replenishment_threshold: values.replenishment_threshold,
          })
        : await replenishmentService.createShelf({
            product_id: values.product_id,
            shelf_id: values.shelf_id,
            inventory_type_id: values.inventory_type_id ?? null,
            capacity: values.capacity,
            replenishment_threshold: values.replenishment_threshold,
          });

      if (response.success) {
        message.success(
          isEdit
            ? t('common.message.update_success')
            : t('common.message.create_success')
        );
        onSaved();
      } else {
        // 後端錯誤碼 (response.message) → handleApiError 翻成 common.error.<code>
        handleApiError(response, {
          t,
          fallbackMessage: isEdit
            ? t('common.error.update_failed')
            : t('common.error.create_failed'),
        });
      }
    } catch (error) {
      handleNetworkError(error, {
        t,
        fallbackMessage: isEdit
          ? t('common.error.update_failed')
          : t('common.error.create_failed'),
      });
    }
  };

  return (
    <Card>
      <Form<SlotFormValues> form={form} layout="vertical" disabled={disabled}>
        <Form.Item
          name="product_id"
          label={t('operator.replenishment_shelves.form.product')}
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <ProductSelect
            productService={productService}
            merchantId={merchantId}
            // 編輯模式不可改商品（product 三元組是 slot 的識別鍵），鎖定。
            disabled={isEdit}
            defaultOptions={productDefaultOptions}
            placeholder={t('operator.replenishment_shelves.form.product_placeholder')}
          />
        </Form.Item>

        <Form.Item
          name="inventory_type_id"
          label={t('operator.replenishment_shelves.form.inventory_type')}
          // 庫存類型是 slot 識別鍵的一部分（與 product + shelf 構成唯一鍵），後端亦必填——
          // 留空送出會被 controller 打成誤導性 404，故前端設為必填。
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <Select
            options={inventoryTypeOptions}
            placeholder={t('operator.replenishment_shelves.form.inventory_type_placeholder')}
          />
        </Form.Item>

        <Form.Item
          name="shelf_id"
          label={t('operator.replenishment_shelves.form.shelf')}
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <ShelfSelect
            // 編輯模式不可改貨架（揀補儲位位置是識別鍵的一部分），鎖定。
            disabled={isEdit}
            initialOption={shelfInitialOption}
            placeholder={t('operator.replenishment_shelves.form.shelf_placeholder')}
          />
        </Form.Item>

        {/* 容量 / 補貨水位 皆為短數字欄位，兩欄並排（對齊 StockTransferOrders/New 的短欄位配對），
            減少垂直滾動並讓表單填滿可用寬度。 */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="capacity"
              label={t('operator.replenishment_shelves.form.capacity')}
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="replenishment_threshold"
              label={t('operator.replenishment_shelves.form.replenishment_threshold')}
              // capacity 改變時重新驗證；門檻必須 < 容量（後端 threshold_below_capacity 也擋，
              // 此處 client 端先擋掉，避免送出後拿到後端驗證錯誤）。
              dependencies={['capacity']}
              rules={[
                { required: true, message: t('common.validation.required') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const cap = getFieldValue('capacity');
                    if (value == null || cap == null || value < cap) return Promise.resolve();
                    return Promise.reject(new Error(t('common.error.must_be_less_than_capacity')));
                  },
                }),
              ]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>
            {t('common.button.cancel')}
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            {t('common.button.save')}
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default SlotForm;
